import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import InspectionDetails from "./InspectionDetails";

const checkListTitle = {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "1rem",
};

const iconStyle = {
    backgroundColor: "#5D5DF61A",
    padding: "1rem",
    borderRadius: "3rem",
    width: "1.5rem",
    display: "flex",
    height: "1.5rem",
    alignItems: "center",
    justifyContent: "center",
};

const titleStyle = {
    color: "#5D5DF6",
    fontSize: "20px",
    fontWeight: "600",
};

const listHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "1rem",
    fontWeight: "600",
    fontSize: "16px",
    color: "#000",
};

const listItemStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    position: "relative",
    borderBottom: "1px solid #f5f5f5",
};

const columnStyle = {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
};

const statusStyle = (isComplete) => ({
    padding: "0.25rem 1rem",
    borderRadius: "5px",
    fontSize: "12px",
    width: "5.5rem",
    textAlign: "center",
    fontWeight: "600",
    backgroundColor: "#fff",
    color: isComplete ? "#10B981" : "#EF4444",
    border: `1px solid ${isComplete ? "#1EAC59" : "#E82222"}`,
});

const moreOptionsStyle = {
    color: "#000000",
    cursor: "pointer",
    fontSize: "2rem",
    width: "100%",
    display: "flex",
    justifyContent: "end",
    paddingRight: "2.5rem",
};

const modalStyle = {
    position: "absolute",
    backgroundColor: "#ffffff",
    padding: "1.2rem",
    borderRadius: "5px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
    top: "3rem",
    right: "6rem",
};

const buttonStyle = (isComplete) => ({
    ...statusStyle(isComplete),
    cursor: "pointer",
    width: "auto",
    padding: "0.5rem",
    whiteSpace: "nowrap",
});

const CheckList = ({ data, title, icon, onUpdate, allData, fetchCleanerStatus }) => {
    const [openModal, setOpenModal] = useState(null);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setOpenModal(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [modalRef]);

    const handleEditClick = (index) => {
        setOpenModal(openModal === index ? null : index);
    };

    const handleStatusChange = (itemName, currentStatus) => {
        onUpdate(itemName, !currentStatus);
        setOpenModal(null);
    };

    const formatTaskName = (name) =>
        name
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();

    const renderListItem = (label, isComplete, index) => (
        <div style={listItemStyle} key={index}>
            <div style={{ ...columnStyle, justifyContent: "flex-start" }}>
                <span style={{ color: "#090948" }}>{formatTaskName(label)}</span>
            </div>
            <div style={{ ...columnStyle, justifyContent: "flex-end" }}>
                <span style={statusStyle(isComplete)}>{isComplete ? "Complete" : "Incomplete"}</span>
            </div>
            <div style={columnStyle}>
                <span onClick={() => handleEditClick(index)} style={moreOptionsStyle}>
                    ...
                </span>
                {openModal === index && (
                    <div ref={modalRef} style={modalStyle}>
                        <button style={buttonStyle(!isComplete)} onClick={() => handleStatusChange(label, isComplete)}>
                            {isComplete ? "Mark as Incomplete" : "Mark as Complete"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    const renderList = (items) => (
        <div>
            <div style={listHeaderStyle}>
                <div style={{ ...columnStyle, justifyContent: "flex-start" }}>
                    <span>Task</span>
                </div>
                <div style={{ ...columnStyle, justifyContent: "flex-end" }}>
                    <span>Current Status</span>
                </div>
                <div style={{ ...columnStyle, justifyContent: "flex-end" }}>
                    <span>Edit Option</span>
                </div>
            </div>
            {items}
        </div>
    );

    return (
        <div>
            <div style={checkListTitle}>
                <div style={iconStyle}>
                    <img src={icon} alt="title" />
                </div>
                <h1 style={titleStyle}>{title}</h1>
            </div>

            {/* Interior */}
            <div>
                {title === "Interior" &&
                    renderList(
                        <>
                            {renderListItem("removeTrash", data?.removeTrash, 0)}
                            {renderListItem("vacuumSeats", data?.vacuumSeats, 1)}
                            {renderListItem("cleanDashboard", data?.cleanDashboard, 2)}
                            {renderListItem("cleanCupHolders", data?.cleanCupHolders, 3)}
                            {renderListItem("wipeWindows", data?.wipeWindows, 4)}
                            {renderListItem("checkSeats", data?.checkSeats, 5)}
                            {renderListItem("cleanAirVents", data?.cleanAirVents, 6)}
                        </>,
                    )}
            </div>

            {/* Exterior */}
            <div>
                {title === "Exterior" &&
                    renderList(
                        <>
                            {renderListItem("checkWheels", data?.checkWheels, 0)}
                            {renderListItem("cleanLights", data?.cleanLights, 1)}
                            {renderListItem("removeBirdDroppings", data?.removeBirdDroppings, 2)}
                            {renderListItem("washExterior", data?.washExterior, 3)}
                            {renderListItem("wipeMirrors", data?.wipeMirrors, 4)}
                        </>,
                    )}
            </div>

            {/* Inspection */}
            <div>
                {title === "Inspection" && (
                    <InspectionDetails data={data} allData={allData} fetchCleanerStatus={fetchCleanerStatus} />
                )}
            </div>

            {/* Final Touches */}
            <div>
                {title === "Final Touch" &&
                    renderList(
                        <>
                            {renderListItem("applyAirFreshener", data?.applyAirFreshener, 0)}
                            {renderListItem("checkSpareTire", data?.checkSpareTire, 1)}
                            {renderListItem("restockSupplies", data?.restockSupplies, 2)}
                            {renderListItem("verifyDocuments", data?.verifyDocuments, 3)}
                        </>,
                    )}
            </div>
        </div>
    );
};

CheckList.propTypes = {
    data: PropTypes.shape({
        // Interior properties
        removeTrash: PropTypes.bool,
        vacuumSeats: PropTypes.bool,
        cleanDashboard: PropTypes.bool,
        cleanCupHolders: PropTypes.bool,
        wipeWindows: PropTypes.bool,
        checkSeats: PropTypes.bool,
        cleanAirVents: PropTypes.bool,
        // Exterior properties
        checkWheels: PropTypes.bool,
        cleanLights: PropTypes.bool,
        removeBirdDroppings: PropTypes.bool,
        washExterior: PropTypes.bool,
        wipeMirrors: PropTypes.bool,
        // Final Touch properties
        applyAirFreshener: PropTypes.bool,
        checkSpareTire: PropTypes.bool,
        restockSupplies: PropTypes.bool,
        verifyDocuments: PropTypes.bool,
    }),
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    onUpdate: PropTypes.func.isRequired,
    allData: PropTypes.object,
    fetchCleanerStatus: PropTypes.func.isRequired,
};

export default CheckList;
